import React from 'react';
import { FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';

// Renders provisional/error chips when the admin stats response includes
// dataQuality flags (revenueSource='estimate', invoiceScanFailed, etc.).
// Returns null when everything is sourced from real invoice data.
const DataQualityBanner = ({ dataQuality }) => {
    if (!dataQuality) return null;

    const warnings = [];
    const failures = [];

    if (dataQuality.apiFailed) {
        failures.push(`Stats API failed — numbers below may be stale${dataQuality.apiError ? `: ${dataQuality.apiError}` : ''}`);
    }
    if (dataQuality.invoiceScanFailed) {
        failures.push('Invoice table unavailable — totals and DD rate are missing');
    } else {
        if (dataQuality.revenueSource === 'estimate') {
            warnings.push('Revenue shown as subscription-MRR estimate (no invoice data yet)');
        }
        if (dataQuality.historySource === 'estimate') {
            warnings.push('Revenue history is estimated from active subscriptions');
        }
        if (dataQuality.ddRateSource === 'none') {
            warnings.push('DD success rate unavailable (no invoice data)');
        }
    }
    if (dataQuality.vendorScanFailed) {
        failures.push('VendorInvoices unavailable — outgoing/payout totals missing');
    }
    if (dataQuality.itServicesScanFailed) {
        failures.push('IT services invoices unavailable');
    }

    // Customer source stats (core-only mode). If PeaknetCoreNBN read fails,
    // the dashboard will be empty — surface that loud and clear.
    const cs = dataQuality.customerSource;
    if (cs) {
        if (cs.coreReadFailed) {
            failures.push('PeaknetCoreNBN customer scan failed — dashboard is empty');
        } else if (cs.mode === 'core_only' && cs.coreCount === 0) {
            warnings.push('No customers in PeaknetCoreNBN (core-only mode is active)');
        }
    }

    if (warnings.length === 0 && failures.length === 0) return null;

    return (
        <div className="space-y-2">
            {failures.length > 0 && (
                <div className="bg-[#3c1f1f] border border-[#cc241d] rounded-lg p-3 flex items-start space-x-2">
                    <FaExclamationTriangle className="text-[#fb4934] mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-[#fbf1c7]">
                        <p className="font-medium text-[#fb4934] mb-1">Data unavailable</p>
                        <ul className="list-disc list-inside space-y-0.5">
                            {failures.map((f) => <li key={f}>{f}</li>)}
                        </ul>
                    </div>
                </div>
            )}
            {warnings.length > 0 && (
                <div className="bg-[#3c3320] border border-[#d79921] rounded-lg p-3 flex items-start space-x-2">
                    <FaInfoCircle className="text-[#fabd2f] mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-[#fbf1c7]">
                        <p className="font-medium text-[#fabd2f] mb-1">Showing estimates</p>
                        <ul className="list-disc list-inside space-y-0.5">
                            {warnings.map((w) => <li key={w}>{w}</li>)}
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataQualityBanner;
